import config from '@config';
import { generateKey, getClassName } from '@utils';
import { useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import type { ChartData } from 'types/common';
import Styled from './BaseChart.styled';

const defaultSpecs = {
  width: config.chart.regular.width,
  height: config.chart.regular.height,
  margin: {
    top: 11,
    right: -24,
    bottom: 1,
    left: 0
  },
  barSize: 50,
  tickSize: 8,
  tickAnchor: 'start' as 'start' | 'end' | 'middle' | 'inherit'
};

export interface Props {
  className?: string;
  colors: readonly string[];
  data: ChartData[];
  domain: [number, number];
  gridHorizontal?: boolean;
  gridVertical?: boolean;
  overridenSpecs?: Partial<typeof defaultSpecs>;
  width?: number;
  height?: number;
  yAxisTicks: number[];
  formatTick: (tick: number, index?: number) => string;
  formatLabel?: (value: string | number) => string | number;
  onSelectDataPoint?: (data: ChartData) => void;
}

function Chart({
  className,
  colors,
  data,
  domain,
  gridHorizontal,
  gridVertical,
  yAxisTicks,
  width,
  height,
  overridenSpecs,
  formatTick,
  formatLabel,
  onSelectDataPoint
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const specs = { ...defaultSpecs, ...(overridenSpecs || {}) };

  // Set the min gap between bars
  const barGap = 2;

  // Calculate bar size dynamically
  const dynamicBarSize = Math.abs(
    (width - specs.margin.right) / data.length - (data.length + 1) * barGap
  );

  // Use the default size as the max threshold for the bar size
  const barSize =
    dynamicBarSize > specs.barSize ? specs.barSize : dynamicBarSize;

  const handleClick = async (data: ChartData, index: number) => {
    // Ensure the component's state is updated and the re-render
    // is over before making any changes to DOM
    await Promise.resolve(onSelectDataPoint?.(data));

    // Remove highlight from the previously selected bar & label
    const previouslySelected =
      wrapperRef.current.querySelectorAll('.highlight');

    if (previouslySelected) {
      for (const element of previouslySelected) {
        element.classList.remove('highlight');
      }
    }

    // Add highlight to the newly selected bar & label
    const currentlySelected = wrapperRef.current?.querySelectorAll(
      `.bar-${index + 1}, .bar-label-${index + 1}`
    );

    for (const element of currentlySelected) {
      element.classList.add('highlight');
    }
  };

  return (
    <ResponsiveContainer width="100%">
      <Styled.Wrapper ref={wrapperRef}>
        <BarChart
          className={getClassName(['compact', 'chart', className])}
          width={width || specs.width}
          height={height || specs.height}
          data={data}
          layout="horizontal"
          margin={{ ...specs.margin }}>
          <CartesianGrid
            className="cartesian-grid-line"
            horizontal={gridHorizontal}
            vertical={gridVertical}
            stroke={'var(--chart-grid-color)'}
            strokeDasharray="2.5 2.5"
          />
          <XAxis
            hide
            type="category"
            fontSize={10}
            padding={{ left: 4, right: 4 }}
          />
          <YAxis
            dataKey="value"
            domain={domain}
            fontSize={12}
            interval={0}
            orientation="right"
            padding={{ top: 10, bottom: 0 }}
            stroke={'var(--chart-grid-color)'}
            tick={{
              fill: 'var(--compact-chart-tick-color)',
              fontSize: 12
            }}
            ticks={yAxisTicks}
            tickFormatter={formatTick}
            tickLine={false}
            tickMargin={0}
            tickSize={specs.tickSize}
            textAnchor={specs.tickAnchor}
            type="number"
          />
          <Bar
            dataKey="value"
            barSize={barSize}
            radius={[4, 4, 0, 0]}
            onClick={handleClick}>
            {data.map((entry, index) => (
              <Cell
                className={getClassName({
                  bar: true,
                  [`bar-${index + 1}`]: true,
                  highlight: entry.isOrigin
                })}
                key={generateKey(`cell-${entry.name}-${entry.value}`)}
                fill={colors.length > 1 ? colors[index] : colors[0]}
              />
            ))}
            <LabelList
              content={({ x, y, value, index }) => {
                const adjustedX = Number(x) + barSize / 2;
                const adjustedY = Number(y) - 5;
                const fontSize = data.length > 4 ? 10 : 12;
                // Rotate each label a bit to improve readability,
                const transform =
                  data.length > 6 // Based on visual clarity testing
                    ? `rotate(-20 ${adjustedX} ${adjustedY})`
                    : '';

                return (
                  <g
                    className={getClassName({
                      'bar-label': true,
                      [`bar-label-${index + 1}`]: true,
                      highlight: data[index].isOrigin
                    })}>
                    <text
                      fill="currentColor"
                      fontSize={fontSize}
                      x={adjustedX}
                      y={adjustedY}
                      transform={transform}
                      textAnchor="middle">
                      {formatLabel?.(value) || Number(value).toFixed(1)}
                    </text>
                  </g>
                );
              }}
            />
          </Bar>
        </BarChart>
      </Styled.Wrapper>
    </ResponsiveContainer>
  );
}

export default Chart;
